import { useState } from "react";
import { siteData, AboutData, AboutValue, AboutMilestone, TeamMember } from "@/store/siteData";
import { saveImage } from "@/utils/imageDB";
import { useImageUrl } from "@/hooks/useImageUrl";

type SubTab = "hero" | "vision" | "values" | "timeline" | "team";

const subTabs: { id: SubTab; label: string; icon: string }[] = [
  { id: "hero", label: "Hero Alanı", icon: "ri-image-line" },
  { id: "vision", label: "Vizyon / Misyon", icon: "ri-eye-line" },
  { id: "values", label: "Değerlerimiz", icon: "ri-shield-star-line" },
  { id: "timeline", label: "Tarihçe", icon: "ri-time-line" },
  { id: "team", label: "Ekip", icon: "ri-team-line" },
];

const iconOptions = ["ri-tools-line","ri-bar-chart-line","ri-scales-line","ri-file-chart-line","ri-time-line","ri-shield-check-line","ri-star-line","ri-award-line","ri-building-2-line","ri-heart-line","ri-lightbulb-line","ri-earth-line","ri-group-line","ri-verified-badge-line","ri-recycle-line","ri-user-star-line"];

function Field({ label, value, onChange, multiline = false, hint }: { label: string; value: string; onChange: (v: string) => void; multiline?: boolean; hint?: string }) {
  const cls = "w-full bg-[#0F1729] border border-[#2B3654] text-white text-sm rounded-lg px-3 py-2.5 outline-none focus:border-[#3B82F6] transition-colors";
  return (
    <div>
      <label className="block text-[#7B8FAF] text-xs uppercase tracking-widest mb-1.5">{label}</label>
      {multiline ? <textarea value={value} onChange={e => onChange(e.target.value)} rows={3} className={cls + " resize-none"} /> : <input type="text" value={value} onChange={e => onChange(e.target.value)} className={cls} />}
      {hint && <p className="text-[#5E7398] text-xs mt-1">{hint}</p>}
    </div>
  );
}

function HeroTab({ data, onChange }: { data: AboutData; onChange: (d: AboutData) => void }) {
  const imgUrl = useImageUrl(data.heroImageId, data.heroImageUrl);
  const [uploading, setUploading] = useState(false);

  const upd = (key: keyof AboutData, val: string) => onChange({ ...data, [key]: val });

  const handleImage = async (file: File) => {
    setUploading(true);
    const id = await saveImage(file);
    onChange({ ...data, heroImageId: id });
    setUploading(false);
  };

  return (
    <div className="space-y-5">
      <div>
        <label className="block text-[#7B8FAF] text-xs uppercase tracking-widest mb-2">Hero Görseli</label>
        <div className="relative h-40 rounded-lg overflow-hidden border border-[#2B3654] mb-3">
          <img src={imgUrl} alt="" className="w-full h-full object-cover object-top" />
        </div>
        <label className="flex items-center gap-3 cursor-pointer border-2 border-dashed border-[#2B3654] hover:border-[#3B82F6] rounded-lg px-4 py-3 transition-colors">
          <div className="w-7 h-7 flex items-center justify-center bg-[#3B82F6]/20 rounded-full flex-shrink-0">
            <i className="ri-upload-cloud-2-line text-[#3B82F6]"></i>
          </div>
          <span className="text-[#7B8FAF] text-sm">{uploading ? "Yükleniyor…" : data.heroImageId ? "Resim yüklendi ✓ — değiştirmek için tıkla" : "PNG / JPG yükle"}</span>
          <input type="file" accept="image/*" className="hidden" onChange={e => e.target.files?.[0] && handleImage(e.target.files[0])} />
        </label>
      </div>
      <Field label="Hero Tagline" value={data.heroTagline} onChange={v => upd("heroTagline", v)} />
      <Field label="Başlık (yeni satır için \\n kullan)" value={data.heroTitle} onChange={v => upd("heroTitle", v)} hint="Örn: 20 Yıllık\nGüçlü Miras" />
      <Field label="Alt Metin" value={data.heroSubtitle} onChange={v => upd("heroSubtitle", v)} />
    </div>
  );
}

function VisionTab({ data, onChange }: { data: AboutData; onChange: (d: AboutData) => void }) {
  const upd = (key: keyof AboutData, val: string) => onChange({ ...data, [key]: val });
  return (
    <div className="space-y-4">
      <Field label="Bölüm Tagline" value={data.visionTagline} onChange={v => upd("visionTagline", v)} />
      <Field label="Başlık (yeni satır için \\n)" value={data.visionTitle} onChange={v => upd("visionTitle", v)} multiline hint="Son satır gri renkte gösterilir" />
      <Field label="1. Paragraf" value={data.visionText1} onChange={v => upd("visionText1", v)} multiline />
      <Field label="2. Paragraf" value={data.visionText2} onChange={v => upd("visionText2", v)} multiline />
      <div className="grid grid-cols-2 gap-4">
        <Field label="İstatistik Değeri (ör: 500+)" value={data.floatStatValue} onChange={v => upd("floatStatValue", v)} />
        <Field label="İstatistik Etiketi" value={data.floatStatLabel} onChange={v => upd("floatStatLabel", v)} />
      </div>
    </div>
  );
}

function ValuesTab({ data, onChange }: { data: AboutData; onChange: (d: AboutData) => void }) {
  const upd = (vals: AboutValue[]) => onChange({ ...data, values: vals });
  const [showIcons, setShowIcons] = useState<number | null>(null);

  const updateVal = (id: number, key: keyof AboutValue, val: string) => {
    upd(data.values.map(v => v.id === id ? { ...v, [key]: val } : v));
  };

  const addValue = () => {
    const newId = Math.max(...data.values.map(v => v.id), 0) + 1;
    upd([...data.values, { id: newId, icon: "ri-star-line", title: "Yeni Değer", desc: "" }]);
  };

  const removeValue = (id: number) => upd(data.values.filter(v => v.id !== id));

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Bölüm Tagline" value={data.valuesTagline} onChange={v => onChange({ ...data, valuesTagline: v })} />
        <Field label="Bölüm Başlığı" value={data.valuesTitle} onChange={v => onChange({ ...data, valuesTitle: v })} />
      </div>

      <div className="space-y-3">
        {data.values.map((val) => (
          <div key={val.id} className="bg-[#0F1729] border border-[#2B3654] rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="relative">
                <button onClick={() => setShowIcons(showIcons === val.id ? null : val.id)} className="flex items-center gap-2 bg-[#182240] border border-[#2B3654] px-3 py-1.5 rounded-lg text-sm text-[#B8C5D8] cursor-pointer hover:border-[#3B82F6]">
                  <div className="w-4 h-4 flex items-center justify-center"><i className={val.icon}></i></div>
                  <span className="text-xs">İkon</span>
                </button>
                {showIcons === val.id && (
                  <div className="absolute top-9 left-0 z-10 bg-[#1D2947] border border-[#2B3654] rounded-lg p-2 grid grid-cols-6 gap-1 w-48">
                    {iconOptions.map(icon => (
                      <button key={icon} onClick={() => { updateVal(val.id, "icon", icon); setShowIcons(null); }} className={`w-7 h-7 flex items-center justify-center rounded cursor-pointer hover:bg-[#3B82F6]/30 ${val.icon === icon ? "bg-[#3B82F6]/40" : ""}`}>
                        <i className={`${icon} text-[#B8C5D8] text-sm`}></i>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <button onClick={() => removeValue(val.id)} className="text-red-400/60 hover:text-red-400 cursor-pointer w-7 h-7 flex items-center justify-center">
                <i className="ri-delete-bin-line text-sm"></i>
              </button>
            </div>
            <input value={val.title} onChange={e => updateVal(val.id, "title", e.target.value)} placeholder="Başlık" className="w-full bg-transparent border-b border-[#2B3654] text-white text-sm py-1 outline-none focus:border-[#3B82F6]" />
            <textarea value={val.desc} onChange={e => updateVal(val.id, "desc", e.target.value)} rows={2} placeholder="Açıklama" className="w-full bg-transparent border-b border-[#2B3654] text-[#7B8FAF] text-xs py-1 outline-none focus:border-[#3B82F6] resize-none" />
          </div>
        ))}
      </div>
      <button onClick={addValue} className="w-full py-2.5 border border-dashed border-[#2B3654] hover:border-[#3B82F6] text-[#7B8FAF] hover:text-[#B8C5D8] text-sm rounded-lg transition-all cursor-pointer">
        <i className="ri-add-line mr-2"></i>Değer Ekle
      </button>
    </div>
  );
}

function TimelineTab({ data, onChange }: { data: AboutData; onChange: (d: AboutData) => void }) {
  const upd = (ms: AboutMilestone[]) => onChange({ ...data, milestones: ms });

  const updateMs = (id: number, key: keyof AboutMilestone, val: string) => {
    upd(data.milestones.map(m => m.id === id ? { ...m, [key]: val } : m));
  };

  const addMs = () => {
    const newId = Math.max(...data.milestones.map(m => m.id), 0) + 1;
    upd([...data.milestones, { id: newId, year: "2025", event: "" }]);
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Bölüm Tagline" value={data.timelineTagline} onChange={v => onChange({ ...data, timelineTagline: v })} />
        <Field label="Bölüm Başlığı" value={data.timelineTitle} onChange={v => onChange({ ...data, timelineTitle: v })} />
      </div>
      <div className="space-y-2">
        {data.milestones.map((m) => (
          <div key={m.id} className="flex items-center gap-3 bg-[#0F1729] border border-[#2B3654] rounded-lg px-4 py-3">
            <input value={m.year} onChange={e => updateMs(m.id, "year", e.target.value)} className="w-16 bg-transparent text-[#B8C5D8] text-sm font-black outline-none border-b border-[#2B3654] focus:border-[#3B82F6]" placeholder="Yıl" />
            <input value={m.event} onChange={e => updateMs(m.id, "event", e.target.value)} className="flex-1 bg-transparent text-[#7B8FAF] text-sm outline-none" placeholder="Olay açıklaması" />
            <button onClick={() => upd(data.milestones.filter(ms => ms.id !== m.id))} className="text-red-400/60 hover:text-red-400 cursor-pointer w-6 h-6 flex items-center justify-center flex-shrink-0">
              <i className="ri-close-line"></i>
            </button>
          </div>
        ))}
      </div>
      <button onClick={addMs} className="w-full py-2.5 border border-dashed border-[#2B3654] hover:border-[#3B82F6] text-[#7B8FAF] hover:text-[#B8C5D8] text-sm rounded-lg transition-all cursor-pointer">
        <i className="ri-add-line mr-2"></i>Kilometre Taşı Ekle
      </button>
    </div>
  );
}

function TeamMemberRow({ member, onUpdate, onDelete }: { member: TeamMember; onUpdate: (m: TeamMember) => void; onDelete: () => void }) {
  const imgUrl = useImageUrl(member.imageId, member.imageUrl);
  const [uploading, setUploading] = useState(false);

  const handleImage = async (file: File) => {
    setUploading(true);
    const id = await saveImage(file);
    onUpdate({ ...member, imageId: id });
    setUploading(false);
  };

  return (
    <div className="bg-[#0F1729] border border-[#2B3654] rounded-lg p-4">
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0">
          <div className="w-14 h-14 rounded-lg overflow-hidden border border-[#2B3654] mb-2">
            <img src={imgUrl} alt={member.name} className="w-full h-full object-cover object-top" />
          </div>
          <label className="block text-center cursor-pointer text-[#5E7398] hover:text-[#3B82F6] text-xs transition-colors">
            <i className="ri-upload-line"></i>{uploading ? " …" : " Resim"}
            <input type="file" accept="image/*" className="hidden" onChange={e => e.target.files?.[0] && handleImage(e.target.files[0])} />
          </label>
        </div>
        <div className="flex-1 space-y-2">
          <input value={member.name} onChange={e => onUpdate({ ...member, name: e.target.value })} className="w-full bg-transparent border-b border-[#2B3654] text-white text-sm font-semibold outline-none focus:border-[#3B82F6] pb-1" placeholder="Ad Soyad" />
          <input value={member.title} onChange={e => onUpdate({ ...member, title: e.target.value })} className="w-full bg-transparent border-b border-[#2B3654] text-[#7B8FAF] text-xs outline-none focus:border-[#3B82F6] pb-1" placeholder="Unvan" />
          <input value={member.department} onChange={e => onUpdate({ ...member, department: e.target.value })} className="w-full bg-transparent border-b border-[#2B3654] text-[#5E7398] text-xs outline-none focus:border-[#3B82F6] pb-1" placeholder="Departman" />
        </div>
        <button onClick={onDelete} className="text-red-400/60 hover:text-red-400 cursor-pointer w-7 h-7 flex items-center justify-center flex-shrink-0">
          <i className="ri-delete-bin-line text-sm"></i>
        </button>
      </div>
    </div>
  );
}

function TeamTab({ data, onChange }: { data: AboutData; onChange: (d: AboutData) => void }) {
  const addMember = () => {
    const newId = Math.max(...(data.team || []).map(m => m.id), 0) + 1;
    onChange({ ...data, team: [...(data.team || []), { id: newId, name: "Yeni Üye", title: "Unvan", department: "Departman", imageUrl: "https://readdy.ai/api/search-image?query=professional%20corporate%20executive%20portrait%20studio%20dark%20background%20suit&width=400&height=500&seq=newteam&orientation=portrait" }] });
  };

  return (
    <div className="space-y-4">
      <Field label="Ekip Başlığı" value={data.teamTitle} onChange={v => onChange({ ...data, teamTitle: v })} />
      <div className="space-y-3">
        {(data.team || []).map(member => (
          <TeamMemberRow
            key={member.id}
            member={member}
            onUpdate={updated => onChange({ ...data, team: data.team.map(m => m.id === member.id ? updated : m) })}
            onDelete={() => onChange({ ...data, team: data.team.filter(m => m.id !== member.id) })}
          />
        ))}
      </div>
      <button onClick={addMember} className="w-full py-2.5 border border-dashed border-[#2B3654] hover:border-[#3B82F6] text-[#7B8FAF] hover:text-[#B8C5D8] text-sm rounded-lg transition-all cursor-pointer">
        <i className="ri-add-line mr-2"></i>Ekip Üyesi Ekle
      </button>
    </div>
  );
}

export default function AboutEditor() {
  const [data, setData] = useState<AboutData>(siteData.getAbout());
  const [activeTab, setActiveTab] = useState<SubTab>("hero");
  const [saved, setSaved] = useState(false);

  const handleSave = () => {
    siteData.saveAbout(data, "Güncellendi", `${subTabs.find(t => t.id === activeTab)?.label} düzenlendi`);
    window.dispatchEvent(new Event("storage"));
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white tracking-tight">Hakkımızda Sayfası</h1>
          <p className="text-[#7B8FAF] text-sm mt-0.5">Ekip, değerler, tarihçe ve hero alanını düzenle</p>
        </div>
        <button onClick={handleSave} className={`px-5 py-2.5 text-sm font-medium rounded-lg transition-all cursor-pointer whitespace-nowrap ${saved ? "bg-emerald-500 text-white" : "bg-[#3B82F6] hover:bg-[#3d4e6a] text-white"}`}>
          {saved ? "✓ Kaydedildi" : "Kaydet"}
        </button>
      </div>

      <div className="flex gap-1 bg-[#0F1729] border border-[#2B3654] rounded-xl p-1 flex-wrap">
        {subTabs.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-all cursor-pointer whitespace-nowrap ${activeTab === tab.id ? "bg-[#3B82F6] text-white" : "text-[#7B8FAF] hover:text-white hover:bg-white/5"}`}>
            <div className="w-4 h-4 flex items-center justify-center"><i className={tab.icon}></i></div>
            {tab.label}
          </button>
        ))}
      </div>

      <div className="bg-[#141E35] border border-[#2B3654] rounded-xl p-6">
        {activeTab === "hero" && <HeroTab data={data} onChange={setData} />}
        {activeTab === "vision" && <VisionTab data={data} onChange={setData} />}
        {activeTab === "values" && <ValuesTab data={data} onChange={setData} />}
        {activeTab === "timeline" && <TimelineTab data={data} onChange={setData} />}
        {activeTab === "team" && <TeamTab data={data} onChange={setData} />}
      </div>

      <div className="bg-[#0F1729] border border-[#1E2B4A] rounded-xl p-4 flex items-center gap-3">
        <div className="w-8 h-8 flex items-center justify-center bg-[#3B82F6]/20 rounded-lg flex-shrink-0">
          <i className="ri-information-line text-[#3B82F6]"></i>
        </div>
        <p className="text-[#5E7398] text-xs leading-relaxed">Değişiklikler <strong className="text-[#7B8FAF]">Kaydet</strong> butonuna bastıktan sonra sitede anında yansır. Farklı sekmelerde değişiklik yaptıktan sonra kaydetmeyi unutmayın.</p>
      </div>
    </div>
  );
}
